/**
 * External Imports
 */
import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useRef,
  useState
} from 'react';
import { connect, useDispatch } from 'react-redux';

/**
 * Internal Imports
 */
import TemplateTreeTable from '../../../../components/customTables/admin/templateTreeTable';
import Loading from '../../../../components/shared/Loading';

import { RootState } from '../../../../redux/store';
import { getQuestionBank } from '../../../../redux/actions';
import { useGetTemplateTreeQuery } from '../../../../redux/apis/Templates.api';

/**
 * Global Type Definition Imports
 */
import {
  AuthUserProps,
  ICondensedMasterTemplate,
  IFullTemplate,
  TreeNode
} from '../../../../config/interfaces';

/**
 * Style Imports
 */
import { TreeContainer } from './TemplatesTree.styles';

interface TemplatesTreeProps {
  authorizedUser: AuthUserProps;
  selectedMasterTemplate: ICondensedMasterTemplate | IFullTemplate | null;
  setSelectedTemplateDocId: Dispatch<SetStateAction<string>>;
  setShowEditTemplateFlag: Dispatch<SetStateAction<boolean>>;
}

const TemplatesTree: FC<TemplatesTreeProps> = ({
  authorizedUser,
  selectedMasterTemplate,
  setSelectedTemplateDocId,
  setShowEditTemplateFlag
}) => {
  const dispatch = useDispatch();

  const [facilityLevelData, setFacilityLevelData] = useState<TreeNode[]>([]);
  const [unitLevelData, setUnitLevelData] = useState<TreeNode[]>([]);
  const [selectedDivision, setSelectedDivision] = useState<string>('');
  const [selectedDivisionId, setSelectedDivisionId] = useState<string>('');
  const [selectedFacility, setSelectedFacility] = useState<string>('');
  const [selectedFacilityId, setSelectedFacilityId] = useState<string>('');

  const divisionTableRef = useRef<null | HTMLDivElement>(null);
  const facilityTableRef = useRef<null | HTMLDivElement>(null);
  const unitTableRef = useRef<null | HTMLDivElement>(null);

  const {
    data: templateTreeData,
    // isError: isGetTemplateTreeError,
    isFetching: isGetTemplateTreeFetching
    // isLoading: isGetTemplateTreeLoading
  } = useGetTemplateTreeQuery(
    {
      userId: authorizedUser.hcaid,
      templateConfigId: selectedMasterTemplate?.templateConfigId ?? ''
    },
    {
      refetchOnMountOrArgChange: 60,
      skip: !selectedMasterTemplate
    }
  );

  const handleTableRowClick = (data: TreeNode, level: string) => {
    if (level === 'Division') {
      setSelectedDivision(data.data.name);
      setSelectedDivisionId(data.data.levelId);
      setFacilityLevelData(
        authorizedUser.access.accessLevel === 'facility'
          ? data.children.filter(
              (child: TreeNode) =>
                authorizedUser.access.accessLevelIds.indexOf(
                  child.data.levelId
                ) !== -1
            )
          : data.children
      );
      setUnitLevelData([]);
      facilityTableRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
    if (level === 'Facility') {
      setSelectedFacility(data.data.name);
      setSelectedFacilityId(data.data.levelId);
      setUnitLevelData(data.children);
      unitTableRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  useEffect(() => {
    dispatch(getQuestionBank());
  }, []);

  useEffect(() => {
    if (templateTreeData && templateTreeData.length > 0) {
      if (selectedDivision !== '') {
        templateTreeData.forEach((item: TreeNode) => {
          if (item.data.levelId === selectedDivisionId) {
            // filter based on access
            const filteredFacilities =
              authorizedUser.access.accessLevel === 'facility'
                ? item.children.filter(
                    (child: TreeNode) =>
                      authorizedUser.access.accessLevelIds.indexOf(
                        child.data.levelId
                      ) !== -1
                  )
                : item.children;
            setFacilityLevelData(filteredFacilities);
          }
        });
      } else {
        setFacilityLevelData([]);
      }
      if (selectedFacility !== '') {
        templateTreeData.forEach((item: TreeNode) => {
          if (item.data.levelId === selectedDivisionId) {
            item.children.forEach((unit: TreeNode) => {
              if (unit.data.levelId === selectedFacilityId) {
                setUnitLevelData(unit.children);
              }
            });
          }
        });
      } else {
        setUnitLevelData([]);
      }
    }
  }, [templateTreeData]);

  return (
    <div style={{ overflowY: 'scroll' }}>
      {isGetTemplateTreeFetching ? (
        <Loading centered={false} />
      ) : (
        <TreeContainer>
          {selectedMasterTemplate && (
            <>
              <div ref={divisionTableRef}>
                <TemplateTreeTable
                  level="Division"
                  treeData={templateTreeData ?? []}
                  handleTableRowClick={handleTableRowClick}
                  roundingType={selectedMasterTemplate?.roundingType ?? ''}
                  setSelectedTemplateDocId={setSelectedTemplateDocId}
                  setShowEditTemplateFlag={setShowEditTemplateFlag}
                />
              </div>
              <div ref={facilityTableRef}>
                <TemplateTreeTable
                  level="Facility"
                  treeData={facilityLevelData ?? []}
                  handleTableRowClick={handleTableRowClick}
                  roundingType={selectedMasterTemplate?.roundingType ?? ''}
                  setSelectedTemplateDocId={setSelectedTemplateDocId}
                  setShowEditTemplateFlag={setShowEditTemplateFlag}
                />
              </div>
              <div ref={unitTableRef}>
                <TemplateTreeTable
                  level="Unit"
                  treeData={unitLevelData ?? []}
                  handleTableRowClick={handleTableRowClick}
                  roundingType={selectedMasterTemplate?.roundingType ?? ''}
                  setSelectedTemplateDocId={setSelectedTemplateDocId}
                  setShowEditTemplateFlag={setShowEditTemplateFlag}
                />
              </div>
            </>
          )}
        </TreeContainer>
      )}
    </div>
  );
};

const mapReduxStateToProps = (state: RootState) => ({
  authorizedUser: state.AuthorizedUser.authorizedUser
});

export default connect(mapReduxStateToProps)(TemplatesTree);
